/* eslint-disable max-lines */
import type { FLAG } from '@shared/products-types';
import type { SecondaryId } from '@api-client/address-book';
import type { Amount } from '@shared/core-types';

export interface Accountno {
    iban?: string;
    'cz-iban'?: string;
    bankCode?: string;
    prefix?: string;
    number?: string;
    bic?: string;
    'cz-bic'?: string;
    countryCode?: string;
    'hr-id'?: string;
}

export interface Receiver extends Accountno {
    secondaryId?: SecondaryId;
    accountId?: string;
    noAccountInfo?: boolean;
}

export interface AdditionalSenderInfo {
    alias?: string;
    fullName?: [string, string, string];
    accountCurrency?: string;
}

export interface Sender extends Accountno {
    additionalInfo?: AdditionalSenderInfo;
    productId?: string;
}

export enum ACTUAL_COLLECTIVE_SIGNEE {
    FIRST,
    SECOND,
}

export interface CollectiveSigningStructure {
    firstSigned: boolean;
    actualSignee: ACTUAL_COLLECTIVE_SIGNEE;
    isSignedByActualUser: boolean;
}

export interface TaxPositionDuty {
    amount: Amount;
    taxType: string;
}

export interface PaymentOrderTaxPosition {
    period: string;
    duties: TaxPositionDuty[];
}

export interface PaymentOrderTax {
    taxNumber?: string;
    positions?: PaymentOrderTaxPosition[];
}

export type TaxPositionPeriod = string;

export interface AdditionalInfo {
    tax: Tax;
}

export interface Tax {
    taxNumber?: string;
    positions?: TaxPosition[];
}

export interface TaxPosition {
    period: TaxPositionPeriod;
    duty: TaxPositionDuty;
}

export interface PaymentOrderAdditionalInfo {
    text4x35?: string[];
    paymentReference?: string;
    tax?: PaymentOrderTax;
}

export enum ORDER_CATEGORY {
    DOMESTIC = 'DOMESTIC',
    SEPA = 'SEPA',
    INTERNATIONAL = 'INTERNATIONAL',
    MIXED = 'MIXED',
    OWN_TRANSFER = 'OWN_TRANSFER',
    OWN_FX_TRANSFER = 'OWN_FX_TRANSFER',
    LOAN_TRANSFER = 'LOAN_TRANSFER',
    LOAN_FX_TRANSFER = 'LOAN_FX_TRANSFER',
}

export enum ORDER_TYPE {
    PAYMENT_OUT = 'PAYMENT_OUT',
    DIRECT_DEBIT_OUT = 'DIRECT_DEBIT_OUT',
    DIRECT_DEBIT_IN = 'DIRECT_DEBIT_IN',
    E_PAYMENT_OUT = 'E_PAYMENT_OUT',
    INSTANT_PAYMENT_OUT = 'INSTANT_PAYMENT_OUT',
    REQUEST_TO_PAY_IN = 'REQUEST_TO_PAY_IN',
    REQUEST_TO_PAY_OUT = 'REQUEST_TO_PAY_OUT',
    INSTANT_E_PAYMENT_OUT = 'INSTANT_E_PAYMENT_OUT',
}

export enum STANDING_ORDER_TYPE {
    SWEEP_ORDER = 'SWEEP_ORDER',
    STANDING_ORDER = 'STANDING_ORDER',
    INSTANT_SWEEP_ORDER = 'INSTANT_SWEEP_ORDER',
    INSTANT_STANDING_ORDER = 'INSTANT_STANDING_ORDER',
    STANDING_ORDER_DIRECT_DEBIT_OUT = 'STANDING_ORDER_DIRECT_DEBIT_OUT',
}

export enum FEE_SHARING_CODE {
    SHA = 'SHA',
    BEN = 'BEN',
    OUR = 'OUR',
}

export enum PRIORITY {
    STANDARD = 'STANDARD',
    URGENT = 'URGENT',
}

export interface Signee {
    userId: string;
    title?: string;
    firstname?: string;
    lastname?: string;
}

export enum CATEGORY_PURPOSE_CODE {
    CASH = 'CASH',
    INTC = 'INTC',
    INTE = 'INTE',
    PENS = 'PENS',
    SALA = 'SALA',
    TAXS = 'TAXS',
    TRAD = 'TRAD',
    TREA = 'TREA',
}

export const CATEGORY_PURPOSE_CODES_SEPA = [
    CATEGORY_PURPOSE_CODE.CASH,
    CATEGORY_PURPOSE_CODE.INTC,
    CATEGORY_PURPOSE_CODE.INTE,
    CATEGORY_PURPOSE_CODE.PENS,
    CATEGORY_PURPOSE_CODE.SALA,
    CATEGORY_PURPOSE_CODE.TAXS,
    CATEGORY_PURPOSE_CODE.TRAD,
    CATEGORY_PURPOSE_CODE.TREA,
];

export const CATEGORY_PURPOSE_CODES_NONSEPA = [
    CATEGORY_PURPOSE_CODE.CASH,
    CATEGORY_PURPOSE_CODE.INTC,
    CATEGORY_PURPOSE_CODE.INTE,
    CATEGORY_PURPOSE_CODE.PENS,
    CATEGORY_PURPOSE_CODE.SALA,
    CATEGORY_PURPOSE_CODE.TAXS,
    CATEGORY_PURPOSE_CODE.TRAD,
    CATEGORY_PURPOSE_CODE.TREA,
];

export const CATEGORY_PURPOSE_CODES_SDD = [
    CATEGORY_PURPOSE_CODE.CASH,
    CATEGORY_PURPOSE_CODE.INTC,
    CATEGORY_PURPOSE_CODE.INTE,
];

type RsPurposeCode =
    | '101'
    | '102'
    | '103'
    | '104'
    | '105'
    | '108'
    | '109'
    | '110'
    | '111'
    | '112'
    | '115'
    | '118'
    | '119'
    | '120'
    | '121'
    | '123'
    | '125'
    | '126'
    | '128'
    | '129'
    | '130'
    | '132'
    | '133'
    | '134'
    | '135'
    | '136'
    | '137'
    | '138'
    | '139'
    | '140'
    | '141'
    | '144'
    | '145'
    | '147'
    | '152'
    | '155'
    | '157'
    | '160'
    | '161'
    | '162'
    | '163'
    | '165'
    | '166'
    | '167'
    | '168'
    | '169'
    | '175'
    | '176'
    | '177'
    | '179'
    | '180'
    | '183'
    | '185'
    | '186'
    | '187'
    | '188'
    | '198'
    | '199'
    | '201'
    | '202'
    | '208'
    | '213'
    | '217'
    | '218'
    | '219'
    | '220'
    | '221'
    | '222'
    | '228'
    | '231'
    | '232'
    | '237'
    | '238'
    | '241'
    | '242'
    | '245'
    | '248'
    | '249'
    | '251'
    | '252'
    | '258'
    | '259'
    | '260'
    | '261'
    | '264'
    | '267'
    | '268'
    | '269'
    | '270'
    | '271'
    | '280'
    | '281'
    | '282'
    | '283'
    | '284'
    | '285'
    | '286'
    | '287'
    | '288'
    | '289'
    | '290'
    | '300'
    | '301'
    | '302'
    | '303'
    | '304'
    | '305'
    | '306'
    | '307'
    | '309'
    | '310'
    | '312'
    | '314'
    | '315'
    | '316'
    | '317'
    | '319'
    | '320'
    | '321'
    | '322'
    | '323'
    | '324'
    | '325'
    | '326'
    | '327'
    | '328'
    | '329'
    | '330'
    | '331'
    | '340'
    | '341'
    | '342'
    | '344'
    | '345'
    | '346'
    | '347'
    | '348'
    | '349'
    | '353'
    | '354'
    | '357'
    | '358'
    | '360'
    | '361'
    | '362'
    | '363'
    | '364'
    | '365'
    | '366'
    | '370'
    | '371'
    | '372'
    | '373'
    | '375'
    | '376'
    | '377'
    | '400'
    | '403'
    | '409'
    | '410'
    | '411'
    | '412'
    | '418'
    | '420'
    | '421'
    | '430'
    | '431'
    | '435'
    | '475'
    | '489'
    | '490'
    | '509'
    | '512'
    | '513'
    | '518'
    | '519'
    | '540'
    | '541'
    | '542'
    | '543'
    | '547'
    | '548'
    | '549'
    | '560'
    | '573'
    | '600'
    | '607'
    | '608'
    | '640'
    | '645'
    | '650'
    | '651'
    | '666'
    | '699'
    | '700'
    | '701'
    | '703'
    | '707'
    | '712'
    | '721'
    | '742'
    | '743'
    | '760'
    | '765'
    | '767'
    | '768'
    | '770'
    | '771'
    | '795'
    | '799'
    | '800'
    | '801'
    | '803'
    | '804'
    | '805'
    | '806'
    | '807'
    | '808'
    | '809'
    | '812'
    | '814'
    | '816'
    | '822'
    | '888'
    | '892'
    | '893'
    | '894'
    | '895'
    | '897'
    | '898'
    | '899'
    | '900'
    | '901'
    | '904'
    | '910'
    | '913'
    | '914';

export type GenericPurposeCode =
    | 'ADVA'
    | 'AGRT'
    | 'AIRB'
    | 'ALMY'
    | 'BECH'
    | 'BONU'
    | 'BUSB'
    | 'CASH'
    | 'CBTV'
    | 'CHAR'
    | 'CLPR'
    | 'COLL'
    | 'CSDB'
    | 'DEPT'
    | 'EDUC'
    | 'ELEC'
    | 'ENRG'
    | 'FAND'
    | 'FEES'
    | 'FORW'
    | 'FXNT'
    | 'GASB'
    | 'GDSV'
    | 'GSCB'
    | 'HLRP'
    | 'HLTI'
    | 'INPC'
    | 'INSU'
    | 'INTC'
    | 'INTE'
    | 'LBRI'
    | 'LIFI'
    | 'LIMA'
    | 'LOAN'
    | 'LOAR'
    | 'NETT'
    | 'PENS'
    | 'PHON'
    | 'PPTI'
    | 'RENT'
    | 'RIMB'
    | 'RLWY'
    | 'SALA'
    | 'SCVE'
    | 'STDY'
    | 'SUBS'
    | 'SUPP'
    | 'TAXS'
    | 'TBIL'
    | 'TELI'
    | 'TRAD'
    | 'TRPT'
    | 'WHLD'
    | 'WTER'
    | 'VATX'
    | 'AREN'
    | 'DBTC';

export type PurposeCode = RsPurposeCode | GenericPurposeCode;

export const PURPOSE_CODES_SEPA: GenericPurposeCode[] = [
    'ADVA',
    'AGRT',
    'AIRB',
    'ALMY',
    'BECH',
    'BONU',
    'BUSB',
    'CASH',
    'CBTV',
    'CHAR',
    'CLPR',
    'COLL',
    'CSDB',
    'DEPT',
    'EDUC',
    'ELEC',
    'ENRG',
    'FAND',
    'FEES',
    'FORW',
    'FXNT',
    'GASB',
    'GDSV',
    'GSCB',
    'HLRP',
    'HLTI',
    'INPC',
    'INSU',
    'INTC',
    'INTE',
    'LBRI',
    'LIFI',
    'LIMA',
    'LOAN',
    'LOAR',
    'NETT',
    'PENS',
    'PHON',
    'PPTI',
    'RENT',
    'RIMB',
    'RLWY',
    'SALA',
    'SCVE',
    'STDY',
    'SUBS',
    'SUPP',
    'TAXS',
    'TBIL',
    'TELI',
    'TRAD',
    'TRPT',
    'WHLD',
    'WTER',
];

export const PURPOSE_CODES_NONSEPA: GenericPurposeCode[] = [
    'ADVA',
    'AGRT',
    'AIRB',
    'ALMY',
    'BECH',
    'BONU',
    'BUSB',
    'CASH',
    'CBTV',
    'CHAR',
    'CLPR',
    'COLL',
    'CSDB',
    'DEPT',
    'EDUC',
    'ELEC',
    'ENRG',
    'FAND',
    'FEES',
    'FORW',
    'FXNT',
    'GASB',
    'GDSV',
    'GSCB',
    'HLRP',
    'HLTI',
    'INPC',
    'INSU',
    'INTC',
    'INTE',
    'LBRI',
    'LIFI',
    'LIMA',
    'LOAN',
    'LOAR',
    'NETT',
    'PENS',
    'PHON',
    'PPTI',
    'RENT',
    'RIMB',
    'RLWY',
    'SALA',
    'SCVE',
    'STDY',
    'SUBS',
    'SUPP',
    'TAXS',
    'TBIL',
    'TELI',
    'TRAD',
    'TRPT',
    'VATX',
    'WHLD',
    'WTER',
];

export const PURPOSE_CODES_SDD: GenericPurposeCode[] = [
    'AGRT',
    'AIRB',
    'AREN',
    'BUSB',
    'CBTV',
    'CHAR',
    'CLPR',
    'COLL',
    'DBTC',
    'EDUC',
    'ELEC',
    'ENRG',
    'FEES',
    'GASB',
    'HLRP',
    'HLTI',
    'INPC',
    'INSU',
    'INTE',
    'LBRI',
    'LIFI',
    'LOAR',
    'PHON',
    'PPTI',
    'RENT',
    'RLWY',
    'SCVE',
    'SUBS',
    'TBIL',
    'TELI',
    'TRAD',
    'TRPT',
    'WTER',
];

export interface CodesToExclude<T> {
    flag?: FLAG;
    condition?(flags: FLAG[], flag: FLAG): boolean;
    codes: T[];
}

export interface CodeOptions<T> {
    possibleCodes: T[];
    codesToExclude?: CodesToExclude<T>[];
}
